import type { ReactNode } from 'react';

import { legacyTheme, styled } from '@prose-ui/legacy';

import { getImageProps, type StaticImageData } from 'Components/Image';

const Root = styled.div<{ backgroundColor: string }>`
  position: relative;
  background-color: ${({ backgroundColor }) => backgroundColor};
  width: 100%;
  height: 100%;
`;

const ImageContainer = styled.div`
  z-index: -1;
  width: 100%;
  height: 100%;
`;

const Children = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 10;
`;

export type Devices<T> = {
  mobile: T;
  tablet: T;
  desktop: T;
};

type BackgroundNextImage2Props = {
  backgroundColor?: string;
  children: ReactNode;
  dataTestId?: string;
  height?: Devices<number>;
  image: Devices<StaticImageData>;
  imageAlt: string;
  priority?: boolean;
  sizebydevice?: Devices<`${number}vw`> | `${number}vw`;
  width?: Devices<number>;
  breakpoints?: Devices<number>;
  fill?: boolean;
};

/**
 * Background image using the next/image component.
 *
 * @priority adds a preload meta tag in the header page that
 * to prefetch the image before loading other assets (CSS, JS, etc.)
 * @sizebydevice generates srcset based on the breakpoint and optimises image size for each viewport size, has {number}vw format (e.g 50vw, where 50 is 50% of the viewport).
 * @height height of the original image in pixels - if not specified next/image computes it, optimization less performant. Not needed for static imports.
 * @width width of the original image in pixels - if not specified next/image computes it, optimization less performant. Not needed for static imports.
 *
 * Documentation for further details: https://nextjs.org/docs/api-reference/next/image
 */
export const BackgroundNextImage = ({
  backgroundColor = 'transparent',
  children,
  dataTestId,
  height,
  image,
  imageAlt,
  priority,
  sizebydevice = '100vw',
  width,
  fill,
  /* TODO(kaike): Add support do new design system breakpoints */
  breakpoints = {
    mobile: 0,
    tablet: legacyTheme.breakpoints.values.sm,
    desktop: legacyTheme.breakpoints.values.md,
  },
}: BackgroundNextImage2Props) => {
  const sizes =
    typeof sizebydevice === 'string'
      ? sizebydevice
      : `(min-width: ${breakpoints.desktop}px) ${sizebydevice.desktop}vw, (min-width: ${breakpoints.tablet}px) ${sizebydevice.tablet}vw, ${sizebydevice.mobile}vw`;
  const common = { alt: imageAlt, sizebydevice: sizes, priority, fill };

  const {
    props: { srcSet: desktopSrcSet },
  } = getImageProps({
    ...common,
    src: image.desktop,
    height: height?.desktop,
    width: width?.desktop,
  });
  const {
    props: { srcSet: tabletSrcSet },
  } = getImageProps({
    ...common,
    src: image.tablet,
    height: height?.tablet,
    width: width?.tablet,
  });
  const {
    props: { srcSet: mobileSrcSet, ...rest },
  } = getImageProps({
    ...common,
    src: image.mobile,
    height: height?.mobile,
    width: width?.mobile,
  });

  return (
    <Root backgroundColor={backgroundColor} data-testid={dataTestId}>
      <ImageContainer>
        <picture>
          <source media={`(min-width: ${breakpoints.desktop}px)`} srcSet={desktopSrcSet} />
          <source media={`(min-width: ${breakpoints.tablet}px)`} srcSet={tabletSrcSet} />
          <source srcSet={mobileSrcSet} />
          <img
            alt={imageAlt}
            {...rest}
            style={{
              width: '100%',
              height: fill ? '100%' : 'auto',
              display: 'block',
              objectFit: fill ? 'cover' : undefined,
            }}
          />
        </picture>
      </ImageContainer>

      <Children>{children}</Children>
    </Root>
  );
};
